"use client";

import React, { ReactElement, useState } from "react";
import Backdrop from "@/components/ui/button/backdrop";
import { createPortal } from "react-dom";

interface ButtonProps {
  style?: string;
  title?: string | ReactElement;
  dataCy?: string;
  subtitle?: string | null | ReactElement;
  label?: string | ReactElement;
  onConfirm: [() => void, style: string, label: string, position: number];
  onCancel: [
    (() => void) | null | undefined,
    style: string,
    label: string,
    position: number,
  ];
  isClicked?: boolean;
  children?: React.ReactNode;
  isDisabled?: boolean;
  popUpOnly?: boolean;
  forceOpen?: boolean;
}

const CallToAction: React.FC<ButtonProps> = ({
  style,
  title,
  dataCy,
  subtitle,
  label,
  onConfirm,
  onCancel,
  isClicked,
  children,
  isDisabled,
  popUpOnly,
  forceOpen,
}) => {
  const [isOpen, setIsOpen] = useState(isClicked || false);

  const shouldShowModal = isOpen || forceOpen;

  const handleButtonClick = () => {
    if (isDisabled) return;
    setIsOpen(true);
  };

  const handleConfirm = () => {
    onConfirm[0]();
    setIsOpen(false);
  };

  const handleCancel = () => {
    if (onCancel[0]) onCancel[0]();
    setIsOpen(false);
  };

  // Sort buttons by position to ensure correct order regardless of position values
  const buttons = [
    {
      handler: handleConfirm,
      style: onConfirm[1],
      label: onConfirm[2],
      position: onConfirm[3],
      type: "confirm" as const,
    },
    {
      handler: handleCancel,
      style: onCancel[1],
      label: onCancel[2],
      position: onCancel[3],
      type: "cancel" as const,
    },
  ].sort((a, b) => a.position - b.position);

  return (
    <>
      {!popUpOnly &&
        (children ? (
          <div
            className={`btn ${style} ${
              isDisabled ? "cursor-not-allowed opacity-50" : ""
            }`}
            onClick={handleButtonClick}
            role="button"
            tabIndex={isDisabled ? -1 : 0}
            onKeyDown={(e) => {
              if ((e.key === "Enter" || e.key === " ") && !isDisabled) {
                e.preventDefault();
                handleButtonClick();
              }
            }}
            {...(dataCy ? { "data-cy": dataCy } : {})}
          >
            {children}
          </div>
        ) : (
          <div
            className={`btn ${style} ${
              isDisabled ? "cursor-not-allowed opacity-50" : ""
            }`}
            onClick={handleButtonClick}
            role="button"
            tabIndex={isDisabled ? -1 : 0}
            onKeyDown={(e) => {
              if ((e.key === "Enter" || e.key === " ") && !isDisabled) {
                e.preventDefault();
                handleButtonClick();
              }
            }}
            data-cy={dataCy || "close-button"}
          >
            {label}
          </div>
        ))}
      {shouldShowModal &&
        createPortal(
          <>
            <Backdrop
              isOpen={shouldShowModal}
              setIsOpen={() => {
                setIsOpen(false);
                handleCancel();
              }}
            />
            <div
              className="fixed top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-2/6 min-w-80 h-44 flex flex-col p-8 bg-white rounded border z-50 items-center justify-center"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="">
                <div className="text-s font-semibold text-center text-neutral-700 font-myriad">
                  {title}
                </div>
                <div className="text-xs leading-3 text-center text-neutral-500 font-myriad mt-1">
                  {subtitle}
                </div>
                <div className="flex justify-center w-64 gap-2.5 mt-4 m-auto">
                  {buttons.map((btn, idx) => (
                    <button
                      key={`${btn.type}-${idx}`}
                      className={`btn ${btn.style} p-2`}
                      onClick={btn.handler}
                      data-cy={
                        btn.type === "confirm" ? "confirm-close-button" : ""
                      }
                    >
                      {btn.label}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </>,
          document.body
        )}
    </>
  );
};

export default CallToAction;